import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { CheckCircle, Download, Home, FileText, Loader2, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import api from '../services/api';
import { identifyCustomerByEmail, capture } from '../lib/posthog';
import analytics from '../services/analytics';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OrderSuccessPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [orderData, setOrderData] = useState(null);
  const [downloadLinks, setDownloadLinks] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');

  // Get session_id and order_id from URL params (from Stripe redirect)
  const sessionId = searchParams.get('session_id');
  const orderId = searchParams.get('order_id');

  const pollPaymentStatus = useCallback(async (sid, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000; // 2 seconds

    if (attempts >= maxAttempts) {
      setStatus('error');
      setErrorMessage('Payment verification timed out. Please check your email for confirmation.');
      return;
    }

    try {
      const response = await api.getPaymentStatus(sid);
      
      if (response.payment_status === 'paid') {
        // Payment successful - get order downloads
        setStatus('success');
        
        // Load pending order from localStorage
        const pendingOrder = localStorage.getItem('pending_order');
        if (pendingOrder) {
          const orderInfo = JSON.parse(pendingOrder);
          setOrderData(orderInfo);
          localStorage.removeItem('pending_order');

          // Privacy-conscious identify: hashed email only, no PII transmitted
          if (orderInfo.email) {
            identifyCustomerByEmail(orderInfo.email);
            capture('purchase_completed', {
              order_number: orderInfo.orderNumber,
              total: orderInfo.total,
              item_count: Array.isArray(orderInfo.items) ? orderInfo.items.length : undefined,
            });
          }
          // GA4 purchase event — the primary e-commerce conversion for GA4
          analytics.trackPurchase(
            orderInfo.orderNumber || sid,
            orderInfo.total,
            Array.isArray(orderInfo.items) ? orderInfo.items : []
          );
        }

        // Get download links from response
        if (response.download_links) {
          setDownloadLinks(response.download_links);
        }
      } else if (response.payment_status === 'processing' || response.payment_status === 'pending') {
        // Still processing - poll again
        setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
      } else {
        // Payment failed
        setStatus('error');
        setErrorMessage(response.error || 'Payment was not completed');
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      // Retry on network error
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
      } else {
        setStatus('error');
        setErrorMessage('Could not verify payment status');
      }
    }
  }, []);

  useEffect(() => {
    const checkPaymentAndLoadOrder = async () => {
      // If we have session_id from Stripe redirect
      if (sessionId) {
        await pollPaymentStatus(sessionId);
      } 
      // If we have location state (legacy flow)
      else if (location.state?.orderNumber) {
        setOrderData({
          orderNumber: location.state.orderNumber,
          email: location.state.email,
          total: location.state.total,
          items: location.state.items || []
        });
        setDownloadLinks(location.state.downloadLinks || []);
        setStatus('success');
      }
      // No payment info - redirect to shop
      else {
        setStatus('error');
        setErrorMessage('No order information found');
      }
    };

    checkPaymentAndLoadOrder();
  }, [sessionId, location.state, pollPaymentStatus]);

  const handleDownload = (downloadUrl) => {
    window.open(`${API_URL}${downloadUrl}`, '_blank');
  };

  // Loading state
  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="p-8 text-center">
            <Loader2 className="h-12 w-12 text-teal-600 animate-spin mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Verifying Payment...</h2>
            <p className="text-gray-600">Please wait while we confirm your payment.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state
  if (status === 'error') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="p-8 text-center">
            <div className="bg-red-100 rounded-full p-4 w-20 h-20 mx-auto mb-4 flex items-center justify-center">
              <AlertCircle className="h-10 w-10 text-red-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Payment Issue</h2>
            <p className="text-gray-600 mb-6">{errorMessage}</p>
            <div className="space-y-3">
              <Button 
                className="w-full bg-teal-600 hover:bg-teal-700"
                onClick={() => navigate('/checkout')}
              >
                Try Again
              </Button>
              <Button 
                variant="outline"
                className="w-full"
                onClick={() => navigate('/')}
              >
                Return Home
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success state
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12">
      <div className="max-w-2xl w-full px-4">
        <Card>
          <CardContent className="p-8 text-center">
            <div className="mb-6">
              <div className="bg-emerald-100 rounded-full p-4 w-20 h-20 mx-auto flex items-center justify-center">
                <CheckCircle className="h-12 w-12 text-emerald-600" />
              </div>
            </div>
            
            <h1 className="text-4xl font-bold text-gray-900 mb-4" data-testid="order-success-title">
              Payment Successful!
            </h1>
            
            {orderData?.orderNumber && (
              <p className="text-lg text-gray-600 mb-2">
                Order Number: <span className="font-semibold">{orderData.orderNumber}</span>
              </p>
            )}
            
            <p className="text-lg text-gray-600 mb-8">
              Thank you for your purchase. Your digital products are ready for download.
            </p>

            {/* Download Links Section */}
            {downloadLinks.length > 0 ? (
              <div className="bg-teal-50 border border-teal-200 rounded-lg p-6 mb-8" data-testid="download-section">
                <div className="flex items-center justify-center gap-2 mb-4">
                  <Download className="h-6 w-6 text-teal-600" />
                  <h3 className="font-semibold text-gray-900 text-lg">Your Downloads</h3>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  Click the buttons below to download your products. Each product has 5 download attempts.
                </p>
                <div className="space-y-3">
                  {downloadLinks.map((link, index) => (
                    <div 
                      key={`dl-${link.product_name}-${index}`} 
                      className="flex items-center justify-between bg-white rounded-lg p-4 border border-teal-100"
                      data-testid={`download-item-${index}`}
                    >
                      <div className="flex items-center gap-3">
                        <FileText className="h-5 w-5 text-teal-600" />
                        <div className="text-left">
                          <p className="font-medium text-gray-900">{link.product_name}</p>
                          <p className="text-xs text-gray-500">{link.downloads_remaining} downloads remaining</p>
                        </div>
                      </div>
                      <Button 
                        size="sm" 
                        className="bg-teal-600 hover:bg-teal-700"
                        onClick={() => handleDownload(link.download_url)}
                        data-testid={`download-btn-${index}`}
                      >
                        <Download className="h-4 w-4 mr-1" />
                        Download
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-6 mb-8">
                <div className="flex items-start gap-3">
                  <Download className="h-6 w-6 text-emerald-600 flex-shrink-0 mt-1" />
                  <div className="text-left">
                    <h3 className="font-semibold text-gray-900 mb-2">Download Links Sent!</h3>
                    <p className="text-sm text-gray-600">
                      We&apos;ve sent download links and instructions to {orderData?.email || 'your email address'}. 
                      Check your inbox (and spam folder) for access to your products.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Order Summary */}
            {orderData?.items && orderData.items.length > 0 && (
              <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
                <h4 className="font-semibold text-gray-900 mb-3">Order Summary</h4>
                <div className="space-y-2">
                  {orderData.items.map((item, index) => (
                    <div key={`item-${item.id || item.name}-${index}`} className="flex justify-between text-sm">
                      <span className="text-gray-600">{item.name} x {item.quantity}</span>
                      <span className="font-medium">${(item.price * item.quantity).toFixed(2)}</span>
                    </div>
                  ))}
                  {orderData.total && (
                    <>
                      <div className="border-t pt-2 mt-2 flex justify-between">
                        <span className="font-semibold">Total</span>
                        <span className="font-semibold text-teal-600">${orderData.total.toFixed(2)}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            <div className="space-y-3">
              <Button 
                size="lg" 
                className="w-full bg-emerald-600 hover:bg-emerald-700"
                onClick={() => navigate('/')}
                data-testid="back-home-btn"
              >
                <Home className="h-5 w-5 mr-2" />
                Back to Home
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="w-full"
                onClick={() => navigate('/shop')}
                data-testid="continue-shopping-btn"
              >
                Continue Shopping
              </Button>
            </div>

            <div className="mt-8 pt-8 border-t border-gray-200">
              <p className="text-sm text-gray-600">
                Need help? Contact us at{' '}
                <a href="mailto:support@goodhabitsfamily.com" className="text-emerald-600 hover:underline">
                  support@goodhabitsfamily.com
                </a>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default OrderSuccessPage;
